import { find } from 'lodash';
import { Observable } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
  AfterViewInit,
  ElementRef,
  inject,
  NgZone,
} from '@angular/core';

import { ComoAsset } from '@core/models/como.model';

import { CartService } from '@core/services/cart/cart.service';
import { ComoService } from '@core/services/como/como.service';

import { DestroyerBase } from '@core/base/destroyer/destroyer.base';

@Component({
  selector: 'app-como-asset-list-atom',
  templateUrl: './como-asset-list-atom.component.html',
  standalone: false,
})
export class ComoAssetListAtomComponent extends DestroyerBase implements OnChanges, AfterViewInit {
  @Input() assets!: ComoAsset[];
  @Input() heading = 'como.loyalty-rewards';
  @Input() subheading?: string;
  @Input() displayPoints = true;
  @Input() displayCredits = true;
  @Input() displayAddButton = true;
  @Input() pointsBalance?: number;
  @Input() creditBalance?: number;
  @Input() displayRedeemableGifts = true;
  @Input() displayNotRedeemableGifts = true;
  @Input() displayRedeemablePointShop = true;
  @Input() displayNotRedeemablePointShop = true;
  @Input() displayNotUsablePointShop = true;
  @Output() onAssetAdd = new EventEmitter<ComoAsset>();
  @Output() onAssetRemove = new EventEmitter<ComoAsset>();

  @ViewChild('scrollContainer', { static: false }) scrollContainerRef!: ElementRef<HTMLDivElement>;

  showDesktopScrollbar = false;
  thumbWidthPercent = 100;
  thumbLeftPercent = 0;

  private readonly ngZone = inject(NgZone);
  private readonly desktopMedia =
    typeof window === 'undefined' ? null : window.matchMedia('(min-width: 993px)');
  private readonly resizeObserver = new ResizeObserver(() => {
    this.ngZone.run(() => this.updateScrollbar());
  });
  private observedRow?: HTMLElement;
  private scrollbarDragging = false;

  redeemableAssets: ComoAsset[] = [];
  noRedeemableAssets: ComoAsset[] = [];
  redeemablePointShops: ComoAsset[] = [];
  noRedeemablePointShops: ComoAsset[] = [];
  pointShops: ComoAsset[] = [];

  constructor(
    private readonly cartService: CartService,
    private readonly comoService: ComoService,
  ) {
    super();
  }

  ngOnChanges(): void {
    this.redeemableAssets = [];
    this.noRedeemableAssets = [];
    this.redeemablePointShops = [];
    this.noRedeemablePointShops = [];
    this.pointShops = [];

    for (const asset of this.assets) {
      const isPointShop = asset.key?.startsWith('ps_');
      if (asset.is_usable) {
        if (asset.status?.toLowerCase() !== 'active') {
          continue;
        }
        if (asset.redeemable === true) {
          if (isPointShop) {
            this.redeemablePointShops.push(asset);
          } else {
            this.redeemableAssets.push(asset);
          }
        } else {
          if (isPointShop) {
            this.noRedeemablePointShops.push(asset);
          } else {
            this.noRedeemableAssets.push(asset);
          }
        }
      } else {
        this.pointShops.push(asset);
      }
    }
  }

  @ViewChild('assetsRow')
  set assetsRowRef(ref: ElementRef<HTMLElement> | undefined) {
    if (this.observedRow) {
      this.resizeObserver.unobserve(this.observedRow);
      this.observedRow = undefined;
    }
    if (ref) {
      this.observedRow = ref.nativeElement;
      this.resizeObserver.observe(ref.nativeElement);
    }
    this.updateScrollbar();
  }

  ngAfterViewInit(): void {
    this.enableHorizontalScrollOnDesktop();
    this.resizeObserver.observe(this.scrollContainerRef.nativeElement);
    this.desktopMedia?.addEventListener('change', this.onDesktopChange);
    this.updateScrollbar();
  }

  override ngOnDestroy(): void {
    this.resizeObserver.disconnect();
    this.desktopMedia?.removeEventListener('change', this.onDesktopChange);
    super.ngOnDestroy();
  }

  onAssetsScroll(): void {
    if (!this.scrollbarDragging) {
      this.updateScrollbar();
    }
  }

  onScrollbarPointerDown(event: PointerEvent): void {
    const track = event.currentTarget as HTMLElement;
    track.setPointerCapture(event.pointerId);
    this.scrollbarDragging = true;
    this.scrollFromPointer(event, track);
  }

  onScrollbarPointerMove(event: PointerEvent): void {
    if (!this.scrollbarDragging) {
      return;
    }
    this.scrollFromPointer(event, event.currentTarget as HTMLElement);
  }

  onScrollbarPointerUp(): void {
    this.scrollbarDragging = false;
    this.updateScrollbar();
  }

  private readonly onDesktopChange = (): void => {
    this.ngZone.run(() => this.updateScrollbar());
  };

  private updateScrollbar(): void {
    const el = this.scrollContainerRef?.nativeElement;
    if (!el) {
      return;
    }
    const maxScroll = el.scrollWidth - el.clientWidth;
    const overflows = maxScroll > 1;
    this.showDesktopScrollbar = !!this.desktopMedia?.matches && overflows;
    if (!overflows) {
      this.thumbWidthPercent = 100;
      this.thumbLeftPercent = 0;
      return;
    }
    const rawWidth = (el.clientWidth / el.scrollWidth) * 100;
    this.thumbWidthPercent = Math.min(100, Math.max(rawWidth, 12));
    const travel = 100 - this.thumbWidthPercent;
    this.thumbLeftPercent = travel * (el.scrollLeft / maxScroll);
  }

  private scrollFromPointer(event: PointerEvent, track: HTMLElement): void {
    const el = this.scrollContainerRef?.nativeElement;
    if (!el) {
      return;
    }
    const rect = track.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const maxScroll = el.scrollWidth - el.clientWidth;
    const thumbRatio = this.thumbWidthPercent / 100;
    el.scrollLeft = ((ratio - thumbRatio / 2) / (1 - thumbRatio)) * maxScroll;
    this.updateScrollbar();
  }

  private enableHorizontalScrollOnDesktop() {
    const el = this.scrollContainerRef.nativeElement;

    el.addEventListener(
      'wheel',
      (e: WheelEvent) => {
        if (e.deltaY !== 0) {
          e.preventDefault(); // prevent vertical scroll
          el.scrollLeft += e.deltaY; // scroll horizontally instead
        }
      },
      { passive: false },
    );
  }

  isAssetInCart$(asset: ComoAsset): Observable<boolean> {
    return this.comoService.getComoUser$().pipe(
      takeUntil(this._destroyerRef),
      map(() => {
        const coupons = this.cartService.getCouponsInCart();

        return !!find(coupons, coupon => coupon.key && coupon.key === asset.key);
      }),
    );
  }
}
